import { useEffect, useState } from "react";
import { firestore } from "../shared/FirebaseConfig";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { useUser } from "../shared/UserContext";

// Order classes by grade: Kindergarten first, then 1→12, then non-grade names (a
// teacher last name like "Binkert", or "Math") last. "K…" → 0, "3B"/"5M" → 3/5.
function gradeRank(name) {
  const s = (name || "").trim();
  if (/^k/i.test(s)) return 0;
  const m = s.match(/^(\d{1,2})(?!\d)/);
  if (m) { const g = +m[1]; if (g >= 1 && g <= 12) return g; }
  return 999;
}

export default function useFetchClasses() {
  const { uid, loading, schoolId, specialistMode } = useUser();
  const [owned, setOwned] = useState([]);
  const [coTaught, setCoTaught] = useState([]);
  const [other, setOther] = useState([]);
  const [schoolWide, setSchoolWide] = useState([]);
  const [classPrefs, setClassPrefs] = useState({});
  const [error, setError]         = useState(null);

  // Listen to every class this teacher is part of — owned, co-taught, or "other"
  // — matching how the dashboard lists them, so co-taught classes also show up in
  // sign-out, attendance, and reporting.
  useEffect(() => {
    if (loading) return;
    if (!uid) { setOwned([]); setCoTaught([]); setOther([]); return; }

    const listen = (field, op, setter) => onSnapshot(
      query(collection(firestore, "classes"), where(field, op, uid)),
      snap => setter(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err  => { console.error("[useFetchClasses] error:", err); setError(err); }
    );

    const unsubs = [
      listen("teacherId", "==", setOwned),
      listen("coTeachers", "array-contains", setCoTaught),
      listen("otherTeachers", "array-contains", setOther),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [uid, loading]);

  // Specialists (art/music/nurse/…) see EVERY class in the school so they can sign
  // any kid out by name from any room. Same-school class reads are already allowed
  // by the rules (like Attendance's school-wide query). Off → no extra reads.
  useEffect(() => {
    if (loading || !specialistMode || !schoolId) { setSchoolWide([]); return; }
    return onSnapshot(
      query(collection(firestore, "classes"), where("schoolId", "==", schoolId)),
      snap => setSchoolWide(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err  => { console.error("[useFetchClasses] school-wide error:", err); setError(err); }
    );
  }, [specialistMode, schoolId, loading]);

  // Listen to the user's per-class preferences
  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(doc(firestore, "users", uid), snap => {
      setClassPrefs(snap.data()?.classPrefs || {});
    });
    return () => unsub();
  }, [uid]);

  // Ids of the teacher's OWN classes (owned, co-taught, or "other") — as opposed to
  // the specialist-only school-wide extras. Lets consumers (e.g. Attendance's
  // "show only my classes") tell the two apart after the merge.
  const mineIds = new Set([...owned, ...coTaught, ...other].map(c => c.id));

  // Merge the three sources (dedup), fold in prefs, and sort.
  const seen = new Set();
  const merged = [];
  for (const cls of [...owned, ...coTaught, ...other, ...schoolWide]) {
    if (!seen.has(cls.id)) { seen.add(cls.id); merged.push(cls); }
  }

  const classList = merged
    // Archived classes are kept for history but must never appear in the active
    // pickers this hook feeds (add-students, attendance, sign-out, checklist, …).
    // ClassList filters them the same way; this centralizes it for every consumer.
    .filter(cls => !cls.isArchived)
    .map(cls => {
      const prefs = classPrefs[cls.id];
      return {
        ...cls,
        isOwner: cls.teacherId === uid,
        // True for the teacher's own classes; false for classes present only because
        // specialist mode pulls in the whole school.
        isMine: mineIds.has(cls.id),
        attendanceEnabled:   prefs?.attendanceEnabled   ?? (cls.attendanceEnabled   ?? true),
        attendanceSortOrder: prefs?.attendanceSortOrder ?? (cls.attendanceSortOrder ?? Infinity),
      };
    })
    // Homeroom leads the list (attendance/sign-out/checklist pickers), then the
    // teacher's manual order.
    .sort((a, b) => {
      const byGrade = () => {
        const ra = gradeRank(a.className), rb = gradeRank(b.className);
        if (ra !== rb) return ra - rb;
        return (a.className || "").localeCompare(b.className || "", undefined, { numeric: true });
      };
      // Specialists see the WHOLE school — order strictly by grade (K→5, then A–Z),
      // ignoring per-class manual order (which only makes sense for a teacher's own
      // handful of classes).
      if (specialistMode) return byGrade();
      // Regular teachers: homeroom first, then their manual order, then grade as a tiebreak.
      if (!!b.isHomeroom !== !!a.isHomeroom) return a.isHomeroom ? -1 : 1;
      if (a.attendanceSortOrder !== b.attendanceSortOrder) return a.attendanceSortOrder - b.attendanceSortOrder;
      return byGrade();
    });

  return { classList, error };
}
